import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  TablePagination,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  Receipt,
  Event,
  Person,
  AttachMoney,
  TrendingDown,
  CalendarMonth,
} from "@mui/icons-material";
import api from "../../services/api";
import { formatDateShort, getCurrentDateForInput } from "../../utils/dateUtils";
import MobileDataCard, {
  MobileDataList,
} from "../../components/common/MobileDataCard";

const AdminGastos = () => {
  // ...existing code...
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Estados
  const [gastos, setGastos] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Filtros y paginación
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEvento, setFilterEvento] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal
  const [openModal, setOpenModal] = useState(false);
  const [editingGasto, setEditingGasto] = useState(null);
  const [formData, setFormData] = useState({
    descripcion: "",
    monto: "",
    fecha: getCurrentDateForInput(),
    evento_id: "",
    colaborador_id: "",
  });
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gastoToDelete, setGastoToDelete] = useState(null);

  // Cargar datos
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [gastosRes, eventosRes, colabsRes] = await Promise.all([
        api.get("/gastos"),
        api.get("/eventos"),
        api.get("/colaboradores"),
      ]);

      console.log("[AdminGastos] Respuesta gastos:", gastosRes.data);

      // Múltiples formas de parsear según la estructura de la API
      const gastosData =
        gastosRes.data?.data?.gastos ||
        gastosRes.data?.gastos ||
        gastosRes.data?.data ||
        (Array.isArray(gastosRes.data) ? gastosRes.data : []);

      console.log("[AdminGastos] Gastos parseados:", gastosData);

      setGastos(Array.isArray(gastosData) ? gastosData : []);
      setEventos(eventosRes.data?.data?.eventos || eventosRes.data?.data || []);
      setColaboradores(colabsRes.data?.data || []);
    } catch (err) {
      console.error("Error cargando datos:", err);
      setError("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!gastoToDelete) return;

    try {
      await api.delete(`/gastos/${gastoToDelete.id}`);
      setSuccess("Gasto eliminado correctamente");
      setDeleteDialogOpen(false);
      setGastoToDelete(null);
      loadData();
    } catch (err) {
      console.error("Error eliminando gasto:", err);
      setError(err.response?.data?.message || "Error al eliminar el gasto");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Computed
  const gastosFiltrados = gastos.filter((g) => {
    const matchSearch =
      !searchTerm ||
      g.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEvento =
      !filterEvento || String(g.evento_id) === String(filterEvento);
    return matchSearch && matchEvento;
  });

  const totalGastos = gastos.reduce(
    (acc, g) => acc + (parseFloat(g.monto) || 0),
    0,
  );

  // Handlers Modal
  const handleOpenCreate = () => {
    setEditingGasto(null);
    setFormData({
      descripcion: "",
      monto: "",
      fecha: getCurrentDateForInput(),
      evento_id: "",
      colaborador_id: "",
    });
    setOpenModal(true);
  };

  const handleOpenEdit = (gasto) => {
    setEditingGasto(gasto);
    setFormData({
      descripcion: gasto.descripcion || "",
      monto: gasto.monto || "",
      fecha: gasto.fecha || getCurrentDateForInput(),
      evento_id: gasto.evento_id || "",
      colaborador_id: gasto.colaborador_id || "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingGasto(null);
  };

  const handleSave = async () => {
    if (!formData.descripcion.trim() || !formData.monto) {
      setError("Descripción y monto son requeridos");
      return;
    }
    try {
      setSaving(true);
      if (editingGasto) {
        await api.put(`/gastos/${editingGasto.id}`, formData);
        setSuccess("Gasto actualizado correctamente");
      } else {
        await api.post("/gastos", formData);
        setSuccess("Gasto creado correctamente");
      }
      handleCloseModal();
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // Renderizado
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "1.25rem", sm: "2.125rem" },
          }}
        >
          Gestión de Gastos
        </Typography>
        {/* Botón desktop */}
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          sx={{ display: { xs: "none", sm: "flex" } }}
        >
          Nuevo Gasto
        </Button>
        {/* Botón móvil */}
        <IconButton
          onClick={handleOpenCreate}
          sx={{
            display: { xs: "flex", sm: "none" },
            width: 44,
            height: 44,
            background: "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
            color: "#fff",
            boxShadow: "0 3px 8px rgba(25, 118, 210, .4)",
            "&:hover": {
              background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
            },
          }}
        >
          <Add />
        </IconButton>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
            }}
          >
            <CardContent sx={{ color: "white" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TrendingDown sx={{ fontSize: 40, opacity: 0.8 }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total Gastos
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    ${totalGastos.toLocaleString("es-MX")}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Receipt sx={{ fontSize: 40, color: "primary.main" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Registros
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {gastosFiltrados.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar gastos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              select
              label="Filtrar por evento"
              value={filterEvento}
              onChange={(e) => setFilterEvento(e.target.value)}
            >
              <MenuItem value="">Todos los eventos</MenuItem>
              {eventos.map((evento) => (
                <MenuItem key={evento.id} value={evento.id}>
                  {evento.titulo}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Vista Móvil */}
      {isMobile ? (
        <Box>
          <MobileDataList
            loading={loading}
            emptyMessage="No hay gastos registrados"
          >
            {gastosFiltrados
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((gasto) => (
                <MobileDataCard
                  key={gasto.id}
                  title={gasto.descripcion || "Sin descripción"}
                  subtitle={`-$${parseFloat(gasto.monto || 0).toLocaleString("es-MX")}`}
                  avatarColor="#f44336"
                  primaryInfo={[
                    {
                      icon: <CalendarMonth fontSize="small" />,
                      label: "Fecha",
                      value: gasto.fecha ? formatDateShort(gasto.fecha) : "-",
                    },
                    {
                      icon: <Event fontSize="small" />,
                      label: "Evento",
                      value: gasto.evento_titulo || "Sin evento",
                    },
                  ]}
                  expandedInfo={[
                    {
                      icon: <Person fontSize="small" />,
                      label: "Colaborador",
                      value: gasto.colaborador_nombre || "-",
                    },
                    {
                      icon: <AttachMoney fontSize="small" />,
                      label: "Monto",
                      value: `$${parseFloat(gasto.monto || 0).toLocaleString("es-MX")}`,
                    },
                  ]}
                  actions={[
                    {
                      icon: <Edit fontSize="small" />,
                      label: "Editar",
                      onClick: () => handleOpenEdit(gasto),
                    },
                    {
                      icon: <Delete fontSize="small" />,
                      label: "Eliminar",
                      color: "error",
                      onClick: () => {
                        setGastoToDelete(gasto);
                        setDeleteDialogOpen(true);
                      },
                    },
                  ]}
                />
              ))}
          </MobileDataList>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={gastosFiltrados.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Box>
      ) : (
        /* Vista Desktop - Tabla */
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: "primary.main" }}>
              <TableRow sx={{ "& th": { color: "white", fontWeight: "bold" } }}>
                <TableCell>Descripción</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Evento</TableCell>
                <TableCell>Colaborador</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gastosFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No hay gastos registrados
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                gastosFiltrados
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((gasto) => (
                    <TableRow key={gasto.id} hover>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Receipt color="error" fontSize="small" />
                          {gasto.descripcion}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{ fontWeight: "bold", color: "error.main" }}
                        >
                          -$
                          {parseFloat(gasto.monto || 0).toLocaleString("es-MX")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {gasto.fecha ? formatDateShort(gasto.fecha) : "-"}
                      </TableCell>
                      <TableCell>
                        {gasto.evento_titulo ? (
                          <Chip
                            icon={<Event fontSize="small" />}
                            label={gasto.evento_titulo}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Sin evento
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {gasto.colaborador_nombre ? (
                          <Chip
                            icon={<Person fontSize="small" />}
                            label={gasto.colaborador_nombre}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEdit(gasto)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setGastoToDelete(gasto);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={gastosFiltrados.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Gastos por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </TableContainer>
      )}

      {/* Modal Crear/Editar */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingGasto ? "Editar Gasto" : "Nuevo Gasto"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    descripcion: e.target.value,
                  }))
                }
                placeholder="Ej: Transporte, materiales, comida..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monto"
                type="number"
                value={formData.monto}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, monto: e.target.value }))
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha"
                type="date"
                value={formData.fecha}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, fecha: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Evento (opcional)"
                value={formData.evento_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    evento_id: e.target.value,
                  }))
                }
              >
                <MenuItem value="">Sin evento</MenuItem>
                {eventos.map((evento) => (
                  <MenuItem key={evento.id} value={evento.id}>
                    {evento.titulo}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Colaborador (opcional)"
                value={formData.colaborador_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    colaborador_id: e.target.value,
                  }))
                }
              >
                <MenuItem value="">Sin colaborador</MenuItem>
                {colaboradores.map((colab) => (
                  <MenuItem key={colab.id} value={colab.id}>
                    {colab.nombre_completo}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Confirmar Eliminación */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar el gasto "
            {gastoToDelete?.descripcion}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminGastos;

// Removed unused import of React
